import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Check, CheckCircle2, Truck, FileText, Sparkles, Building2,
  Lock, ArrowRight, Printer, Receipt, ShieldCheck, X,
} from "lucide-react";
import { toast } from "sonner";

/* ──────────────────────────────────────────────────────────────────────────
   PublicPO — supplier-facing, no-auth view of a PO sent through verqo.
   The supplier doesn't need an account to view, acknowledge, attach
   tracking, or upload an invoice. But every screen pushes them toward
   "Claim your free seller account" because that's the conversion path.

   Route: /p/po/:token  (public; mounted outside the auth shell)
   ────────────────────────────────────────────────────────────────────── */
const STATUS_PROGRESS = ["sent", "acknowledged", "shipped", "invoiced", "paid"];

export default function PublicPO() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Dialogs
  const [ackOpen, setAckOpen] = useState(false);
  const [trkOpen, setTrkOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);

  // Forms
  const [ackForm, setAckForm] = useState({ note: "" });
  const [trkForm, setTrkForm] = useState({ carrier: "", tracking_no: "", note: "" });
  const [invForm, setInvForm] = useState({
    invoice_number: "", amount: "",
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    note: "",
    file: null,         // File object
    fileMeta: null,     // { name, type, size }
  });
  const [paidForm, setPaidForm] = useState({ method: "ach", paid_on: new Date().toISOString().slice(0, 10), note: "" });
  const [claimForm, setClaimForm] = useState({ name: "", company: "", password: "" });
  const [claimResult, setClaimResult] = useState(null);

  const load = () => {
    api.get(`/public/po/${token}`)
      .then(({ data }) => setData(data))
      .catch(e => setError(e.response?.data?.detail || "PO not found"));
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-8 bg-secondary/30">
        <div className="max-w-md text-center rounded-2xl bg-white border border-border p-10">
          <div className="display text-2xl tracking-tight">PO not found</div>
          <p className="text-sm text-foreground/55 mt-2">This link is invalid or has been removed by the buyer.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen grid place-items-center text-foreground/55 text-sm">Loading…</div>;
  }

  const { po, supplier_account_exists } = data;
  const stage = po.status || "sent";
  const stageIdx = STATUS_PROGRESS.indexOf(stage);
  const itemList = po.items || [];
  const acknowledged = !!po.supplier_acknowledged_at;
  const shipped = !!po.supplier_tracking;
  const invoiced = !!po.supplier_invoice_id;
  const paid = po.status === "paid";

  // Read a File into base64 so we can JSON-post it. Capped at 10 MB.
  const readFileAsB64 = (file) => new Promise((resolve, reject) => {
    if (file.size > 10 * 1024 * 1024) return reject(new Error("File too large (10 MB max)"));
    const r = new FileReader();
    r.onerror = () => reject(new Error("Could not read file"));
    r.onload = () => {
      const dataUrl = r.result;
      const b64 = dataUrl.split(",")[1] || "";
      resolve(b64);
    };
    r.readAsDataURL(file);
  });

  const onFilePicked = (file) => {
    if (!file) return setInvForm({ ...invForm, file: null, fileMeta: null });
    setInvForm({
      ...invForm,
      file,
      fileMeta: { name: file.name, type: file.type, size: file.size },
    });
  };

  const submitAck = async () => {
    setBusy(true);
    try {
      await api.post(`/public/po/${token}/acknowledge`, { note: ackForm.note || undefined });
      toast.success("Acknowledged · the buyer is notified");
      setAckOpen(false); setAckForm({ note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const submitTracking = async () => {
    if (!trkForm.carrier.trim() || !trkForm.tracking_no.trim()) return toast.error("Carrier + tracking required");
    setBusy(true);
    try {
      await api.post(`/public/po/${token}/tracking`, trkForm);
      toast.success("Tracking attached · buyer notified");
      setTrkOpen(false); setTrkForm({ carrier: "", tracking_no: "", note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const submitInvoice = async () => {
    if (!invForm.amount || Number(invForm.amount) <= 0) return toast.error("Amount must be > 0");
    if (!invForm.due_date) return toast.error("Due date required");
    setBusy(true);
    try {
      let payload = {
        invoice_number: invForm.invoice_number || undefined,
        amount:         Number(invForm.amount),
        due_date:       new Date(invForm.due_date).toISOString(),
        note:           invForm.note || undefined,
      };
      if (invForm.file) {
        const b64 = await readFileAsB64(invForm.file);
        payload = {
          ...payload,
          file_name:     invForm.file.name,
          file_type:     invForm.file.type || "application/pdf",
          file_data_b64: b64,
        };
      }
      await api.post(`/public/po/${token}/upload-invoice`, payload);
      toast.success(invForm.file
        ? "Invoice sent · file attached · buyer can finance it instantly"
        : "Invoice sent · buyer can finance it instantly");
      setInvOpen(false);
      setInvForm({
        invoice_number: "", amount: "",
        due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        note: "", file: null, fileMeta: null,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed");
    } finally { setBusy(false); }
  };
  const submitMarkPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/public/po/${token}/mark-paid`, {
        method:  paidForm.method || undefined,
        paid_on: paidForm.paid_on ? new Date(paidForm.paid_on).toISOString() : undefined,
        note:    paidForm.note || undefined,
      });
      toast.success("Marked as paid · the buyer is notified");
      setPaidOpen(false);
      setPaidForm({ method: "ach", paid_on: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const submitClaim = async () => {
    if (!claimForm.name.trim()) return toast.error("Your name is required");
    if (claimForm.password.length < 8) return toast.error("Password must be 8+ characters");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/po/${token}/claim`, {
        name:     claimForm.name.trim(),
        company:  claimForm.company.trim() || undefined,
        password: claimForm.password,
      });
      // Persist token so the user is logged in immediately
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setClaimResult(data);
      toast.success("Account claimed · welcome to verqo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Top bar */}
      <header className="border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-foreground text-background grid place-items-center display text-lg">v</span>
            <div>
              <div className="display text-lg leading-none tracking-tight">verqo</div>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Purchase order portal</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.15em] text-foreground/45">
            <Lock className="w-3 h-3"/>Secure link · view-only without account
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6 pb-16" data-testid="public-po">

        {/* Buyer envelope */}
        <div className="rounded-2xl bg-white border border-border p-6" data-testid="public-po-envelope">
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">From</div>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center"><Building2 className="w-5 h-5 text-foreground/60"/></div>
            <div>
              <div className="display text-xl tracking-tight">{po.buyer_company || po.buyer_name || "A verqo buyer"}</div>
              <div className="text-xs text-foreground/55 mt-0.5">sent you a purchase order</div>
            </div>
          </div>
        </div>

        {/* PO summary card */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="public-po-card">
          <div className="p-6 border-b border-border bg-foreground text-background">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">Purchase order</div>
                <div className="display text-3xl mt-1 tracking-tight">{po.po_number || "PO"}</div>
                <div className="text-xs opacity-70 mt-1">Issued {new Date(po.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">Total</div>
                <div className="display text-3xl mt-1">{money(po.total)}</div>
                <div className="text-xs opacity-70 mt-1">{po.terms?.toUpperCase()} payment terms</div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-2">For</div>
              <div className="text-sm font-medium">{po.supplier_name}</div>
              {po.supplier_email && <div className="text-xs text-foreground/55">{po.supplier_email}</div>}
            </div>

            {itemList.length > 0 && (
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-2">Line items</div>
                <div className="rounded-xl border border-border divide-y divide-border">
                  {itemList.map((it, i) => (
                    <div key={i} className="p-3 flex items-center justify-between text-sm">
                      <div>
                        <div>{it.name}</div>
                        {it.qty && <div className="text-xs text-foreground/55 mt-0.5">{it.qty} × {money(it.price || 0)}</div>}
                      </div>
                      <div className="display">{money((it.price || 0) * (it.qty || 1))}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {po.note && (
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5">Note from buyer</div>
                <div className="text-sm bg-secondary/40 rounded-xl p-3 italic">"{po.note}"</div>
              </div>
            )}

            {/* Progress strip */}
            <div className="pt-2">
              <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-2">Progress</div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="public-po-progress">
                {STATUS_PROGRESS.map((s, i) => {
                  const done = i <= stageIdx;
                  return (
                    <div key={s} className="flex items-center gap-1.5">
                      <div className={`flex items-center gap-1.5 px-3 h-7 rounded-full text-[11px] mono uppercase tracking-wide
                        ${done ? "bg-foreground text-background" : "bg-secondary text-foreground/45"}`}>
                        {done && <Check className="w-3 h-3"/>}{s.replace("_", " ")}
                      </div>
                      {i < STATUS_PROGRESS.length - 1 && <div className={`w-3 h-px ${done ? "bg-foreground/30" : "bg-border"}`}/>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid md:grid-cols-3 gap-3" data-testid="public-po-actions">
          {!acknowledged && (
            <Button onClick={() => setAckOpen(true)} data-testid="ack-btn"
              className="h-14 rounded-2xl bg-foreground text-background hover:bg-foreground/90">
              <CheckCircle2 className="w-4 h-4 mr-2"/>Acknowledge PO
            </Button>
          )}
          {acknowledged && !shipped && (
            <Button onClick={() => setTrkOpen(true)} data-testid="trk-btn"
              className="h-14 rounded-2xl bg-foreground text-background hover:bg-foreground/90">
              <Truck className="w-4 h-4 mr-2"/>Add tracking
            </Button>
          )}
          {(shipped || acknowledged) && !invoiced && (
            <Button onClick={() => setInvOpen(true)} data-testid="inv-btn"
              variant={shipped ? "default" : "outline"}
              className={shipped
                ? "h-14 rounded-2xl bg-foreground text-background hover:bg-foreground/90"
                : "h-14 rounded-2xl"}>
              <Receipt className="w-4 h-4 mr-2"/>Upload invoice
            </Button>
          )}
          {invoiced && !paid && (
            <Button onClick={() => setPaidOpen(true)} data-testid="paid-btn"
              className="h-14 rounded-2xl bg-foreground text-background hover:bg-foreground/90">
              <CheckCircle2 className="w-4 h-4 mr-2"/>Confirm payment received
            </Button>
          )}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/public/po/${token}/pdf`}
            target="_blank" rel="noopener noreferrer"
            data-testid="download-pdf-btn"
            className="h-14 rounded-2xl bg-white border border-border text-foreground hover:border-foreground/30 inline-flex items-center justify-center font-medium text-sm px-4">
            <Printer className="w-4 h-4 mr-2"/>Download PO (PDF)
          </a>
        </div>

        {/* Already-completed acknowledgements / tracking / invoice */}
        {acknowledged && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3 text-sm">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5"/>
            <div>
              <div className="font-medium">Acknowledged on {new Date(po.supplier_acknowledged_at).toLocaleDateString()}</div>
              {po.supplier_acknowledgement_note && <div className="text-xs text-foreground/65 mt-1 italic">"{po.supplier_acknowledgement_note}"</div>}
            </div>
          </div>
        )}
        {shipped && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 text-sm">
            <Truck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
            <div>
              <div className="font-medium">Shipped via {po.supplier_tracking.carrier}</div>
              <div className="text-xs text-foreground/65 mt-0.5 mono">{po.supplier_tracking.tracking_no}</div>
              {po.supplier_tracking.note && <div className="text-xs text-foreground/65 mt-1 italic">"{po.supplier_tracking.note}"</div>}
            </div>
          </div>
        )}
        {invoiced && !paid && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3 text-sm" data-testid="invoiced-card">
            <Receipt className="w-5 h-5 text-blue-700 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-medium">Invoice sent · the buyer can finance it instantly</div>
              <div className="text-xs text-foreground/65 mt-0.5">You'll be paid within 24h once they accept. Once funds land in your account, hit <b>Confirm payment received</b> below to close out this PO.</div>
            </div>
          </div>
        )}
        {paid && po.supplier_paid_record && (
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 flex items-start gap-3 text-sm" data-testid="paid-card">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-medium">Payment received · {new Date(po.supplier_paid_record.paid_on).toLocaleDateString()}</div>
              <div className="text-xs text-foreground/65 mt-0.5">
                {po.supplier_paid_record.method ? <>Paid via <b>{po.supplier_paid_record.method.toUpperCase()}</b>. </> : null}
                This PO is closed. The buyer has been notified.
              </div>
              {po.supplier_paid_record.note && <div className="text-xs text-foreground/55 italic mt-1">"{po.supplier_paid_record.note}"</div>}
            </div>
          </div>
        )}

        {/* THE conversion play — claim free verqo seller account */}
        {!po.claimed_seller_id && !supplier_account_exists && (
          <div className="rounded-2xl bg-foreground text-background p-7 mt-2 relative overflow-hidden" data-testid="claim-banner">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-accent/10 blur-3xl"/>
            <div className="relative">
              <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-[0.2em] opacity-70">
                <Sparkles className="w-3 h-3"/>Claim your free supplier portal
              </div>
              <h2 className="display text-3xl mt-2 tracking-tight">Get paid faster. Run your wholesale on verqo.</h2>
              <p className="text-sm opacity-80 mt-2 max-w-xl">
                {po.buyer_company || po.buyer_name} sent you this PO via verqo. Claim a free seller account to manage all your wholesale buyers in one place — get paid in 24h via instant invoice financing, see live shipment tracking, and connect any number of additional buyers.
              </p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {[
                  ["Get paid in 24h",   "Buyers can finance any invoice instantly."],
                  ["No more chasing",   "Real-time PO + payment status."],
                  ["Connect any buyer", "One portal across your whole book."],
                  ["Free, forever",     "Pay only on transactions."],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-background/10 backdrop-blur p-3">
                    <div className="font-medium">{k}</div>
                    <div className="opacity-70 mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button onClick={() => setClaimOpen(true)} data-testid="claim-cta"
                  className="rounded-full bg-background text-foreground hover:bg-background/90 px-6 h-11">
                  Claim your free portal <ArrowRight className="w-4 h-4 ml-1.5"/>
                </Button>
                <div className="text-xs opacity-65 inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3"/>Takes 30 seconds. No credit card.
                </div>
              </div>
            </div>
          </div>
        )}

        {(po.claimed_seller_id || supplier_account_exists) && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-center gap-3" data-testid="already-claimed">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0"/>
            <div className="flex-1 text-sm">
              <div className="font-medium">You already have a verqo supplier account</div>
              <div className="text-xs text-foreground/60 mt-0.5">Sign in to access your full portal — orders, shipments, payments, and analytics.</div>
            </div>
            <a href="/login?as=seller" className="rounded-full bg-foreground text-background px-4 h-9 text-sm inline-flex items-center" data-testid="signin-cta">Sign in</a>
          </div>
        )}

        <div className="text-center text-[10px] mono uppercase tracking-[0.18em] text-foreground/40 pt-4">
          <Lock className="w-3 h-3 inline -mt-0.5 mr-1"/>Powered by verqo · the wholesale operating system
        </div>
      </main>

      {/* Acknowledge dialog */}
      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent className="max-w-md" data-testid="ack-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Acknowledge this PO</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">Confirms receipt to {po.buyer_company || po.buyer_name}. Add a note if you need to flag anything.</p>
          <Textarea rows={3} value={ackForm.note} onChange={e => setAckForm({ note: e.target.value })}
            placeholder="e.g. 'Confirmed — shipping next Tuesday'" className="mt-3" data-testid="ack-note"/>
          <Button onClick={submitAck} disabled={busy} className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="ack-submit">
            {busy ? "Saving…" : "Acknowledge PO"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Tracking dialog */}
      <Dialog open={trkOpen} onOpenChange={setTrkOpen}>
        <DialogContent className="max-w-md" data-testid="trk-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Add tracking</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <Input placeholder="Carrier (UPS, FedEx, …)" value={trkForm.carrier}
              onChange={e => setTrkForm({...trkForm, carrier: e.target.value})} data-testid="trk-carrier"/>
            <Input placeholder="Tracking number" value={trkForm.tracking_no}
              onChange={e => setTrkForm({...trkForm, tracking_no: e.target.value})} data-testid="trk-no"/>
            <Textarea rows={2} placeholder="Note (optional)" value={trkForm.note}
              onChange={e => setTrkForm({...trkForm, note: e.target.value})} data-testid="trk-note"/>
          </div>
          <Button onClick={submitTracking} disabled={busy} className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="trk-submit">
            {busy ? "Saving…" : "Attach tracking"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="max-w-md" data-testid="inv-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Upload invoice</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">Buyer receives an instant financing offer. You're paid within 24h once they accept.</p>
          <div className="space-y-3 mt-3">
            <Input placeholder="Invoice number (optional)" value={invForm.invoice_number}
              onChange={e => setInvForm({...invForm, invoice_number: e.target.value})} data-testid="inv-num"/>
            <Input type="number" min="0" step="0.01" placeholder="Amount *" value={invForm.amount}
              onChange={e => setInvForm({...invForm, amount: e.target.value})} data-testid="inv-amt"/>
            <Input type="date" value={invForm.due_date}
              onChange={e => setInvForm({...invForm, due_date: e.target.value})} data-testid="inv-due"/>
            <Textarea rows={2} placeholder="Note (optional)" value={invForm.note}
              onChange={e => setInvForm({...invForm, note: e.target.value})} data-testid="inv-note"/>

            {/* File upload — optional invoice attachment */}
            <div>
              <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">
                Invoice file <span className="text-foreground/40 normal-case">(optional · PDF or image, max 10 MB)</span>
              </label>
              {!invForm.fileMeta ? (
                <label htmlFor="inv-file-input" data-testid="inv-file-label"
                  className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border hover:border-foreground/40 hover:bg-secondary/30 transition cursor-pointer p-5 text-sm text-foreground/60">
                  <Receipt className="w-4 h-4"/>
                  Click to attach a PDF or image
                  <input id="inv-file-input" type="file"
                    accept="application/pdf,image/png,image/jpeg,image/webp,image/heic"
                    onChange={e => onFilePicked(e.target.files?.[0])}
                    className="hidden" data-testid="inv-file-input"/>
                </label>
              ) : (
                <div className="rounded-xl border border-border p-3 flex items-center justify-between text-sm" data-testid="inv-file-attached">
                  <div className="flex items-center gap-2 min-w-0">
                    <Receipt className="w-4 h-4 text-foreground/60 shrink-0"/>
                    <div className="min-w-0">
                      <div className="truncate">{invForm.fileMeta.name}</div>
                      <div className="text-[11px] text-foreground/50 mono">{(invForm.fileMeta.size / 1024).toFixed(1)} KB · {invForm.fileMeta.type || "PDF"}</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => onFilePicked(null)} data-testid="inv-file-remove"
                    className="text-foreground/40 hover:text-rose-600 p-1">
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              )}
            </div>
          </div>
          <Button onClick={submitInvoice} disabled={busy} className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="inv-submit">
            {busy ? "Sending…" : "Send invoice"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Claim dialog */}
      <Dialog open={claimOpen} onOpenChange={(v) => { if (!v && claimResult) { window.location.href = "/seller/dashboard"; } setClaimOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="claim-dialog">
          {!claimResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="display text-2xl tracking-tight">Claim your free verqo portal</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">
                We'll create a free seller account at <span className="mono font-medium">{po.supplier_email}</span>. Auto-connects you to {po.buyer_company || po.buyer_name}.
              </p>
              <div className="space-y-3 mt-3">
                <Input placeholder="Your name *" value={claimForm.name}
                  onChange={e => setClaimForm({...claimForm, name: e.target.value})} data-testid="claim-name"/>
                <Input placeholder={`Company name (default: ${po.supplier_name})`} value={claimForm.company}
                  onChange={e => setClaimForm({...claimForm, company: e.target.value})} data-testid="claim-company"/>
                <Input type="password" placeholder="Choose a password (8+ chars)" value={claimForm.password}
                  onChange={e => setClaimForm({...claimForm, password: e.target.value})} data-testid="claim-password"/>
              </div>
              <Button onClick={submitClaim} disabled={busy} data-testid="claim-submit"
                className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                {busy ? "Creating…" : <>Claim portal <ArrowRight className="w-4 h-4 ml-1.5"/></>}
              </Button>
              <p className="text-[10px] text-center text-foreground/45 mt-2 inline-flex items-center gap-1 justify-center w-full">
                <ShieldCheck className="w-3 h-3"/>Free forever · no credit card · 30 seconds
              </p>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="display text-2xl tracking-tight">Welcome to verqo 🎉</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">
                Your supplier portal is live. {claimResult.po.buyer_name} is now connected — future POs from them sync in real time.
              </p>
              <Button onClick={() => window.location.href = "/seller/dashboard"} data-testid="claim-go-portal"
                className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                Open my portal <ArrowRight className="w-4 h-4 ml-1.5"/>
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark-as-paid dialog */}
      <Dialog open={paidOpen} onOpenChange={setPaidOpen}>
        <DialogContent className="max-w-md" data-testid="paid-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Confirm payment received</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">Closes this PO out and notifies {po.buyer_company || po.buyer_name}. Use this once funds have actually landed in your bank account.</p>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">Payment date</label>
              <Input type="date" value={paidForm.paid_on}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setPaidForm({...paidForm, paid_on: e.target.value})} data-testid="paid-date"/>
            </div>
            <div>
              <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">Payment method</label>
              <div className="grid grid-cols-4 gap-2" data-testid="paid-method-grid">
                {[
                  { v: "ach",    l: "ACH" },
                  { v: "wire",   l: "Wire" },
                  { v: "check",  l: "Check" },
                  { v: "card",   l: "Card" },
                ].map(opt => (
                  <button key={opt.v} type="button"
                    onClick={() => setPaidForm({...paidForm, method: opt.v})}
                    data-testid={`paid-method-${opt.v}`}
                    className={`text-xs rounded-xl border px-3 py-2 transition ${paidForm.method === opt.v
                      ? "bg-foreground text-background border-foreground"
                      : "bg-white text-foreground/70 border-border hover:border-foreground/30"}`}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <Textarea rows={2} placeholder="Note (optional · e.g. 'Wire ref #ABC123')" value={paidForm.note}
              onChange={e => setPaidForm({...paidForm, note: e.target.value})} data-testid="paid-note"/>
          </div>
          <Button onClick={submitMarkPaid} disabled={busy} data-testid="paid-submit"
            className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            {busy ? "Saving…" : <><CheckCircle2 className="w-4 h-4 mr-1.5"/>Confirm payment</>}
          </Button>
          <p className="text-[10px] text-center text-foreground/45 mt-2">
            Closes the PO. Buyer's records auto-update to "Paid".
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
